import { CloseButton } from '@/components/ui/close-button';
import { Box, Drawer, HStack, Portal } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import React, { ReactNode, useEffect, useRef } from 'react';
import {
	LuArrowDown,
	LuArrowUp,
	LuCalendarArrowDown,
	LuCalendarArrowUp,
	LuChevronLeft,
	LuChevronRight,
	LuPanelRightClose,
} from 'react-icons/lu';
import { useRightSidePanel } from '@/context/RightSidePanelContext';
import { useToastObstruction } from '@/hooks/ui/useToastObstruction';
import { TOAST_EDGE_INSET_PX, cssLengthToPx } from '@/utils/toastInsets';
import { Tooltip } from '@/components/ui/tooltip';
import { TRANSITION_BORDER } from '@/components/ui/motion';

/** Matches the `width` default below, for panels whose width is not a plain pixel length. */
const ASSUMED_PANEL_WIDTH_PX = 440;

/**
 * How the panel puts itself on the screen.
 *
 * `drawer` slides in over the page and traps focus, which is right when the panel is a detour from
 * what you were doing. `pane` sits in the layout beside the content as a column of its own — the
 * page stays usable behind it, so nothing is trapped and there is no backdrop to dismiss.
 *
 * The content, header and footer are identical either way. Only the container changes, so a caller
 * switches presentation without rewriting what it renders.
 */
export const PanelPresentations = {
	Drawer: 'drawer',
	Pane: 'pane',
} as const;
export type PanelPresentation = (typeof PanelPresentations)[keyof typeof PanelPresentations];

export interface RightSidePanelNavigation {
	canGoBack: boolean;
	canGoForward: boolean;
	onBack: () => void;
	onForward: () => void;
}

export interface RightSidePanelListNavigation {
	canGoPrevious: boolean;
	canGoNext: boolean;
	onPrevious: () => void;
	onNext: () => void;
}

/**
 * The same thing at a different time — one card's own occasions, not the list it sits in.
 *
 * Its own pair rather than a second meaning for {@link RightSidePanelListNavigation}. Up and down
 * step the list on every other surface, and having them silently mean something else on one tab is
 * how an icon stops being readable. Calendar arrows say "same task, different time" without a label.
 */
export interface RightSidePanelOccasionNavigation {
	canGoPrevious: boolean;
	canGoNext: boolean;
	onPrevious: () => void;
	onNext: () => void;
	previousLabel: string;
	nextLabel: string;
}

interface HeaderNavigationButtonProps {
	label: string;
	tooltip: string;
	icon: React.ElementType;
	disabled: boolean;
	onClick: () => void;
	hoverBg: React.ComponentProps<typeof IconButton>['bg'];
}

const HeaderNavigationButton: React.FC<HeaderNavigationButtonProps> = ({
	label,
	tooltip,
	icon: NavigationIcon,
	disabled,
	onClick,
	hoverBg,
}) => (
	<Tooltip content={tooltip} openDelay={300}>
		<Box as="span" display="inline-flex" cursor="default">
			<IconButton
				aria-label={label}
				variant="ghost"
				size="sm"
				disabled={disabled}
				_hover={{ bg: hoverBg }}
				_disabled={{ cursor: 'default', bg: 'transparent' }}
				onClick={onClick}
			>
				<NavigationIcon />
			</IconButton>
		</Box>
	</Tooltip>
);

/** Stands in for `Drawer.CloseTrigger` in a pane, where the close button carries its own handler. */
const PassThrough: React.FC<{ children: ReactNode; asChild?: boolean }> = ({ children }) => <>{children}</>;

interface RightSidePanelProps {
	/**
	 * Whether the panel is open
	 */
	isOpen: boolean;

	/**
	 * Callback when the panel open state changes
	 */
	onOpenChange: (state: { open: boolean }) => void;

	/**
	 * Content to display in the panel
	 */
	children: ReactNode;

	/**
	 * Title to display in the header
	 */
	title?: ReactNode;

	/**
	 * Footer content to display at the bottom
	 */
	footer?: ReactNode;

	/**
	 * Width of the panel
	 */
	width?: string | number;

	/**
	 * Whether to show the close button
	 */
	showCloseButton?: boolean;

	/**
	 * Additional props to pass to the drawer content
	 */
	contentProps?: React.ComponentProps<typeof Drawer.Content>;

	/** Optional contrasting header surface; controls invert their hover surface to remain visible. */
	headerBg?: React.ComponentProps<typeof Drawer.Header>['bg'];

	/**
	 * Overlay content rendered inside the panel (e.g. a contained bottom sheet).
	 * Positioned absolute inset 0 within Drawer.Content.
	 */
	overlay?: ReactNode;

	/** Optional record-to-record traversal shown in the panel header. */
	navigation?: RightSidePanelNavigation;
	/** Shows the shared record traversal controls, even before the current trail has somewhere to go. */
	showNavigation?: boolean;
	/** Previous/next traversal through the page's currently visible ordered list. */
	listNavigation?: RightSidePanelListNavigation;
	/** One card's own occasions — drawn as a third pair, never folded into `listNavigation`. */
	occasionNavigation?: RightSidePanelOccasionNavigation;
	/** Actions that belong to the drawer as a whole, rendered beside the close control. */
	headerActions?: ReactNode;
	/** Called after the drawer's closing transition has finished. */
	onExitComplete?: () => void;

	/**
	 * Skip the open/close transition (Chakra's `immediate`). Defaults to false so panels animate in and
	 * out consistently; a caller can still pass `immediate` to override for a specific panel.
	 */
	immediate?: boolean;

	/** Defaults to the drawer. `pane` renders inline, for a layout that gives the panel a column. */
	presentation?: PanelPresentation;
}

/**
 * A reusable right side panel component using Chakra UI's Drawer.
 * Automatically registers its open state into RightSidePanelContext for consumers
 * that need to react to an open right-side drawer.
 */
const RightSidePanel: React.FC<RightSidePanelProps> = ({
	isOpen,
	onOpenChange,
	children,
	title,
	footer,
	width = '440px',
	showCloseButton = true,
	contentProps,
	headerBg,
	overlay,
	navigation,
	showNavigation = false,
	listNavigation,
	occasionNavigation,
	headerActions,
	onExitComplete,
	immediate = false,
	presentation = PanelPresentations.Drawer,
}) => {
	const { registerPanel, unregisterPanel, canGoBack, canGoForward, goBack, goForward } = useRightSidePanel();
	const isPane = presentation === PanelPresentations.Pane;
	// The slots differ only in their container element. Chakra's drawer slots carry no styling this
	// component relies on — every box below sets its own — so a plain Box stands in for each.
	const CloseControl = isPane ? PassThrough : Drawer.CloseTrigger;
	const Parts = isPane
		? { Content: Box, Header: Box, Body: Box, Footer: Box }
		: { Content: Drawer.Content, Header: Drawer.Header, Body: Drawer.Body, Footer: Drawer.Footer };
	const contentRef = useRef<HTMLDivElement | null>(null);
	/**
	 * Back and Forward are always drawn, greyed out where there is nowhere to go.
	 *
	 * They were briefly withheld on an empty trail, on the reasoning that a control which can do
	 * nothing should not be offered. That is the wrong trade for this pair: a reader who has never
	 * seen them does not learn the panel can retrace at all, and retracing is the most valuable
	 * thing the header does. Disabled says "not from here"; absent says "never".
	 */
	const panelNavigation =
		navigation ?? (showNavigation ? { canGoBack, canGoForward, onBack: goBack, onForward: goForward } : undefined);
	const headerControlHoverBg = headerBg === 'surface.default' ? 'surface.subtle' : 'surface.default';

	// The panel is full height, so a toast has nothing to stack on and moves left of it instead.
	const panelWidthPx = cssLengthToPx(width) ?? ASSUMED_PANEL_WIDTH_PX;
	useToastObstruction('right', isOpen ? panelWidthPx + TOAST_EDGE_INSET_PX : null);

	useEffect(() => {
		if (isOpen) {
			registerPanel(width);
		} else {
			unregisterPanel();
		}
		return () => unregisterPanel();
	}, [isOpen, width, registerPanel, unregisterPanel]);

	const panel = (
		<Parts.Content
			ref={contentRef}
			data-panel-presentation={presentation}
			// A pane is given its width by the column it sits in; a drawer stands against the viewport
			// edge at its own. Same for height: the pane fills its row, the drawer the whole window.
			maxW={isPane ? 'full' : width}
			w={isPane ? 'full' : undefined}
			bg="surface.subtle"
			h={isPane ? 'full' : '100dvh'}
			borderRadius={isPane ? '3xl' : undefined}
			borderWidth={isPane ? '1px' : undefined}
			borderColor={isPane ? 'border.default' : undefined}
			transition={isPane ? TRANSITION_BORDER : undefined}
			tabIndex={-1}
			display="flex"
			flexDirection="column"
			overflow="hidden"
			position="relative"
			zIndex={isPane ? undefined : 'modal'}
			_focus={{ outline: 'none' }}
			{...contentProps}
		>
			{showCloseButton || panelNavigation || listNavigation || occasionNavigation || headerActions || title ? (
				<Parts.Header
					data-panel-part="header"
					p={0}
					bg={headerBg}
					flexShrink={0}
					display="flex"
					flexDirection="column"
					alignItems="stretch"
				>
					{showCloseButton || panelNavigation || listNavigation || occasionNavigation || headerActions ? (
						<HStack
							justify={
								panelNavigation || listNavigation || occasionNavigation ? 'space-between' : 'flex-end'
							}
							px={4}
							pt={4}
							// Titles bring their own top padding. Pull that box into the close row's
							// unused left side so the two rows retain an 8px visual gap, not 24px.
							mb={-4}
						>
							{panelNavigation || listNavigation || occasionNavigation ? (
								<HStack gap={1}>
									{panelNavigation ? (
										<>
											<HeaderNavigationButton
												label="Go back"
												tooltip="Back"
												icon={LuChevronLeft}
												disabled={!panelNavigation.canGoBack}
												onClick={panelNavigation.onBack}
												hoverBg={headerControlHoverBg}
											/>
											<HeaderNavigationButton
												label="Go forward"
												tooltip="Forward"
												icon={LuChevronRight}
												disabled={!panelNavigation.canGoForward}
												onClick={panelNavigation.onForward}
												hoverBg={headerControlHoverBg}
											/>
										</>
									) : null}
									{panelNavigation && (listNavigation || occasionNavigation) ? (
										<Box w="1px" h={4} bg="border.default" mx={1} />
									) : null}
									{listNavigation ? (
										<>
											<HeaderNavigationButton
												label="Previous item"
												tooltip="Previous item"
												icon={LuArrowUp}
												disabled={!listNavigation.canGoPrevious}
												onClick={listNavigation.onPrevious}
												hoverBg={headerControlHoverBg}
											/>
											<HeaderNavigationButton
												label="Next item"
												tooltip="Next item"
												icon={LuArrowDown}
												disabled={!listNavigation.canGoNext}
												onClick={listNavigation.onNext}
												hoverBg={headerControlHoverBg}
											/>
										</>
									) : null}
									{/* Third group, and separated from the list for the same reason the list is
									    separated from the trail: they answer different questions. */}
									{occasionNavigation && listNavigation ? (
										<Box w="1px" h={4} bg="border.default" mx={1} />
									) : null}
									{occasionNavigation ? (
										<>
											<HeaderNavigationButton
												label={occasionNavigation.previousLabel}
												tooltip={occasionNavigation.previousLabel}
												// Time runs up the History list, so the earlier time is down it.
												icon={LuCalendarArrowDown}
												disabled={!occasionNavigation.canGoPrevious}
												onClick={occasionNavigation.onPrevious}
												hoverBg={headerControlHoverBg}
											/>
											<HeaderNavigationButton
												label={occasionNavigation.nextLabel}
												tooltip={occasionNavigation.nextLabel}
												icon={LuCalendarArrowUp}
												disabled={!occasionNavigation.canGoNext}
												onClick={occasionNavigation.onNext}
												hoverBg={headerControlHoverBg}
											/>
										</>
									) : null}
								</HStack>
							) : null}
							<HStack gap={1}>
								{headerActions}
								{showCloseButton ? (
									<Tooltip content="Close" openDelay={300}>
										<Box as="span" display="inline-flex">
											<CloseControl asChild={!isPane}>
												<CloseButton
													size="sm"
													position="static"
													aria-label="Close drawer"
													_hover={{ bg: headerControlHoverBg }}
													onClick={isPane ? () => onOpenChange({ open: false }) : undefined}
												>
													<LuPanelRightClose />
												</CloseButton>
											</CloseControl>
										</Box>
									</Tooltip>
								) : null}
							</HStack>
						</HStack>
					) : null}
					{title}
				</Parts.Header>
			) : null}

			<Parts.Body
				data-panel-part="body"
				py={4}
				// Real padding owns the inset so it never collapses on overlay/thin scrollbars
				// (Safari, mobile) where the gutter reserves ~0. scrollbar-gutter stays for its
				// actual job — reserving the scrollbar's space symmetrically without shifting content.
				px={{ base: 4, md: 2.5 }}
				flex="1"
				overflowY="auto"
				scrollbarGutter="stable both-edges"
			>
				{children}
			</Parts.Body>

			{footer && (
				<Parts.Footer
					data-panel-part="footer"
					position="relative"
					zIndex={1}
					p={4}
					mx="1px"
					w="calc(100% - 2px)"
					flexShrink={0}
					bg="surface.default"
					borderTopRadius="3xl"
					borderTop="1px solid"
					borderColor="border.default"
				>
					{footer}
				</Parts.Footer>
			)}
			{overlay}
		</Parts.Content>
	);

	if (isPane) return isOpen ? panel : null;

	return (
		<Drawer.Root
			open={isOpen}
			onOpenChange={onOpenChange}
			immediate={immediate}
			preventScroll={false}
			initialFocusEl={() => contentRef.current}
			onExitComplete={onExitComplete}
		>
			<Portal>
				<Drawer.Backdrop />
				<Drawer.Positioner>{panel}</Drawer.Positioner>
			</Portal>
		</Drawer.Root>
	);
};

export default RightSidePanel;
